// =============================================================================
// PEDRA — src/core/defaults.ts
// Valores padrao, paleta de cores, geracao de ids e projetos iniciais.
// =============================================================================

import type {
  EdgeBanding,
  EdgeBandType,
  Id,
  Part,
  Project,
  Settings,
  StockSheet,
} from './types';
import { PROJECT_VERSION } from './types';
import { MICRON_PER_MM } from './units';

// -----------------------------------------------------------------------------
// GERACAO DE IDS
// -----------------------------------------------------------------------------

/**
 * Contador global monotonico. NAO usamos Math.random nem Date.now: ids
 * previsiveis tornam os testes deterministicos e os snapshots estaveis.
 */
let idCounter = 0;

/**
 * Gera um id unico dentro da sessao: `p-1`, `s-2`, `eb-3`...
 * O prefixo e so para legibilidade em depuracao; a unicidade vem do contador.
 */
export function newId(prefix = 'id'): Id {
  idCounter += 1;
  return `${prefix}-${idCounter}`;
}

/**
 * Zera o contador. Use APENAS em testes (`beforeEach`), nunca em producao.
 */
export function resetIdCounter(): void {
  idCounter = 0;
}

/**
 * Avanca o contador para alem de qualquer id numerico ja presente nos dados
 * carregados (localStorage, JSON importado, link compartilhado). Evita que
 * `newId()` devolva um id que ja existe no projeto.
 * Chame isso SEMPRE depois de carregar um projeto de fora.
 */
export function syncIdCounter(ids: Iterable<Id>): void {
  for (const id of ids) {
    const m = /(\d+)$/.exec(id);
    if (!m) continue;
    const n = Number(m[1]);
    if (Number.isFinite(n) && n > idCounter) idCounter = n;
  }
}

/** Percorre um projeto inteiro e sincroniza o contador com todos os ids. */
export function syncIdCounterFromProject(p: Project): void {
  const ids: Id[] = [];
  for (const part of p.parts) ids.push(part.id);
  for (const s of p.stock) ids.push(s.id);
  for (const b of p.edgeBands) ids.push(b.id);
  syncIdCounter(ids);
}

// -----------------------------------------------------------------------------
// PALETA
// -----------------------------------------------------------------------------

/**
 * 16 cores claras e distinguiveis entre si, todas com luminancia alta o
 * bastante para o texto escuro (#1F2933) ficar legivel por cima
 * (contraste >= 7:1 em todas). Ordenadas para que cores vizinhas na lista
 * fiquem visualmente distantes.
 */
export const PART_COLORS: readonly string[] = Object.freeze([
  '#A8D3E8', // azul claro
  '#F7C58C', // laranja claro
  '#B3E0A6', // verde claro
  '#F2A7A2', // salmao
  '#C9BCE8', // lilas
  '#E8D9A8', // areia
  '#F5B8D4', // rosa
  '#9FDCD6', // turquesa
  '#D8E08E', // verde limao
  '#D3BBA3', // bege madeira
  '#B7C7EC', // azul acinzentado
  '#E9B7E0', // lavanda rosada
  '#A9DEC0', // verde menta
  '#F0CBA8', // pessego
  '#CFD6DD', // cinza azulado
  '#E3C9C9', // rosa terroso
]);

/** Cor de texto usada sobre as cores de `PART_COLORS`. */
export const PART_TEXT_COLOR = '#1F2933';

/** Cor da proxima peca, ciclando a paleta pelo indice da linha. */
export function colorForIndex(index: number): string {
  const n = PART_COLORS.length;
  return PART_COLORS[((index % n) + n) % n] as string;
}

// -----------------------------------------------------------------------------
// FITA DE BORDA
// -----------------------------------------------------------------------------

/** Nenhum lado com fita. Sempre devolve um objeto NOVO (nao compartilhar). */
export function emptyEdgeBanding(): EdgeBanding {
  return { top: null, right: null, bottom: null, left: null };
}

// -----------------------------------------------------------------------------
// CONFIGURACOES PADRAO
// -----------------------------------------------------------------------------

/**
 * Padroes de fabrica. A regra e a mesma do CutList Optimizer original: o app
 * abre com TODOS os recursos opcionais DESLIGADOS e a espessura de corte em
 * zero. Assim a primeira visita nunca exige preencher material, veio ou fita —
 * quem quiser cada recurso liga em "Opcoes".
 */
export const DEFAULT_SETTINGS: Readonly<Settings> = Object.freeze({
  kerf: 0, // sem desconto de serra ate o usuario informar o disco
  considerGrain: false,
  considerMaterial: false,
  edgeBandingEnabled: false,
  labelsOnPanels: false,
  useSingleStockUnit: false,
  unit: 'm',
  dimensionOrder: 'lengthFirst',
  // 3 casas em metro = precisao de milimetro (3,070 m = 3070 mm), que e o grao
  // real de quem corta chapa. A grade em si nunca arredonda: ela mostra o valor
  // exato digitado; estas casas governam diagrama, estatisticas e relatorios.
  decimalPlaces: 3,
  minTrimDimension: 0,
  stackSamePanels: 'auto',
  priority: 'leastWaste',
  cutOrientation: 'optimal',
  timeBudgetMs: 8_000,
  seed: 20260730,
} as Settings);

/** Copia mutavel das configuracoes padrao. */
export function createDefaultSettings(): Settings {
  return { ...DEFAULT_SETTINGS };
}

// -----------------------------------------------------------------------------
// FABRICAS DE LINHA
// -----------------------------------------------------------------------------

/** Linha de peca em branco, pronta para a grade. */
export function createEmptyPart(index = 0): Part {
  return {
    id: newId('p'),
    length: 0,
    width: 0,
    qty: 1,
    label: '',
    material: '',
    orientation: 0,
    enabled: true,
    edgeBanding: emptyEdgeBanding(),
    color: colorForIndex(index),
  };
}

/** Linha de chapa em branco, pronta para a grade. */
export function createEmptyStock(): StockSheet {
  return {
    id: newId('s'),
    length: 0,
    width: 0,
    qty: 1,
    label: '',
    material: '',
    orientation: 0,
    enabled: true,
  };
}

/** Tipo de fita em branco. */
export function createEmptyEdgeBandType(): EdgeBandType {
  return {
    id: newId('eb'),
    name: '',
    thickness: MICRON_PER_MM, // 1,0 mm
    color: '#8C8C8C',
  };
}

// -----------------------------------------------------------------------------
// PROJETOS
// -----------------------------------------------------------------------------

/** Projeto vazio: uma linha de peca e uma de chapa, ambas em branco. */
export function createEmptyProject(): Project {
  return {
    version: PROJECT_VERSION,
    name: 'Novo projeto',
    parts: [createEmptyPart(0)],
    stock: [createEmptyStock()],
    settings: createDefaultSettings(),
    edgeBands: [],
  };
}

/** Atalho para escrever medidas em milimetros dentro deste arquivo. */
const mm = (v: number): number => Math.round(v * MICRON_PER_MM);

/**
 * Exemplo realista de marcenaria: um armario de cozinha de 2 portas em MDF
 * 15 mm, cortado de uma chapa padrao brasileira de 2750 x 1830 mm.
 */
export function createSampleProject(): Project {
  const material = 'MDF 15 mm Branco';

  const rows: Array<{
    label: string;
    length: number;
    width: number;
    qty: number;
    orientation?: 0 | 1 | 2;
  }> = [
    { label: 'Lateral', length: 2100, width: 550, qty: 2, orientation: 2 },
    { label: 'Divisória central', length: 2100, width: 540, qty: 1, orientation: 2 },
    { label: 'Base', length: 1160, width: 550, qty: 1 },
    { label: 'Tampo', length: 1160, width: 550, qty: 1 },
    { label: 'Prateleira', length: 570, width: 530, qty: 6 },
    { label: 'Fundo', length: 2100, width: 1190, qty: 1, orientation: 2 },
    { label: 'Porta', length: 2080, width: 578, qty: 2, orientation: 2 },
    { label: 'Travessa frontal', length: 1160, width: 100, qty: 2 },
    { label: 'Gaveta - frente', length: 200, width: 570, qty: 3 },
    { label: 'Gaveta - lateral', length: 500, width: 180, qty: 6 },
    { label: 'Rodapé', length: 1160, width: 150, qty: 1 },
  ];

  const parts: Part[] = rows.map((r, i) => ({
    id: newId('p'),
    length: mm(r.length),
    width: mm(r.width),
    qty: r.qty,
    label: r.label,
    material,
    orientation: r.orientation ?? 0,
    enabled: true,
    edgeBanding: emptyEdgeBanding(),
    color: colorForIndex(i),
  }));

  const stock: StockSheet[] = [
    {
      id: newId('s'),
      length: mm(2750),
      width: mm(1830),
      qty: 4,
      label: 'Chapa 2750 × 1830',
      material,
      orientation: 2,
      enabled: true,
    },
  ];

  return {
    version: PROJECT_VERSION,
    name: 'Armário de cozinha — exemplo',
    parts,
    stock,
    // O exemplo e uma DEMONSTRACAO: ele liga de proposito os recursos que os
    // padroes de fabrica deixam desligados (veio, rotulos) e usa um disco real
    // de 4 mm, para o plano exibido ser o de uma marcenaria de verdade.
    settings: {
      ...DEFAULT_SETTINGS,
      kerf: 4 * MICRON_PER_MM,
      considerGrain: true,
      labelsOnPanels: true,
    },
    edgeBands: [],
  };
}
